import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, ChevronRight, Target, Star, Pencil } from 'lucide-react';
import type {
  CalculatedMetric,
  CampaignMetadata,
  MetricGoal,
  MetricFormat,
  MetricDefinition,
} from '@monitoring-panel/types';
import { FIXED_METRICS } from '@monitoring-panel/types';
import {
  useMetricConfig,
  useAddPredefinedMetric,
  useRemoveMetric,
  useCreateCustomMetric,
  useUpdateCustomMetric,
  useUpdateMetricGoals,
  useToggleStarredMetric,
} from '@/hooks/useMetricConfig';
import { MetricGoalForm } from './MetricGoalForm';
import { AddMetricDropdown } from './AddMetricDropdown';
import { CustomMetricBuilder } from './CustomMetricBuilder';
import { AIMetricSuggestionsModal } from './AIMetricSuggestionsModal';
import { cn } from '@/lib/utils';

interface MetricConfigDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  executionId: string;
  metrics: CalculatedMetric[];
  availableColumns: string[];
  sampleData: Record<string, number>;
  metadata: CampaignMetadata;
  availableMetrics: MetricDefinition[];
  campaignData: Array<Record<string, unknown>>;
}

const fixedMetricIds = new Set(FIXED_METRICS.map((m) => m.id));

export function MetricConfigDrawer({
  isOpen,
  onClose,
  executionId,
  metrics,
  availableColumns,
  sampleData,
  metadata,
  availableMetrics,
  campaignData,
}: MetricConfigDrawerProps) {
  const { data: configData } = useMetricConfig(executionId);
  const addPredefined = useAddPredefinedMetric(executionId);
  const removeMetric = useRemoveMetric(executionId);
  const createCustom = useCreateCustomMetric(executionId);
  const updateCustom = useUpdateCustomMetric(executionId);
  const updateGoals = useUpdateMetricGoals(executionId);
  const toggleStarred = useToggleStarredMetric(executionId);

  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const [localGoals, setLocalGoals] = useState<Record<string, MetricGoal>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState<MetricDefinition | null>(null);
  const [isAISuggestOpen, setIsAISuggestOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Sync local goals from config
  useEffect(() => {
    if (configData?.config?.metricGoals) {
      setLocalGoals(configData.config.metricGoals);
      setIsDirty(false);
    }
  }, [configData?.config?.metricGoals]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleGoalChange = (metricId: string, goal: MetricGoal | undefined) => {
    setLocalGoals((prev) => {
      const next = { ...prev };
      if (goal) {
        next[metricId] = goal;
      } else {
        delete next[metricId];
      }
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = () => {
    updateGoals.mutate(localGoals, {
      onSuccess: () => setIsDirty(false),
    });
  };

  const enabledMetrics = [
    ...(configData?.config?.enabledOptionalMetrics || []),
    ...(configData?.config?.customMetrics?.map((m) => m.id) || []),
  ];

  const handleCreateCustom = (metric: {
    name: string;
    formula: string;
    format: MetricFormat;
    decimals: number;
  }) => {
    createCustom.mutate(metric);
    setIsBuilderOpen(false);
  };

  const handleEditMetric = (customMetric: MetricDefinition) => {
    setEditingMetric(customMetric);
    setIsBuilderOpen(true);
  };

  const handleUpdateCustom = (
    metricId: string,
    metric: { name: string; formula: string; format: MetricFormat; decimals: number }
  ) => {
    updateCustom.mutate({ metricId, metric });
    setIsBuilderOpen(false);
    setEditingMetric(null);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 adv-overlay"
    >
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-md bg-adv-card border-l border-adv-border shadow-adv-elevation-3 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-adv-border">
          <h2 className="adv-section-title">Metric Configuration</h2>
          <button
            onClick={onClose}
            className="adv-btn adv-btn--ghost adv-btn--icon adv-btn--sm"
          >
            <X size={20} />
          </button>
        </div>

        {/* Metric list */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2">
          {metrics.map((metric) => {
            const isFixed = fixedMetricIds.has(metric.id);
            const isExpanded = expandedMetric === metric.id;
            const hasGoal = !!localGoals[metric.id];
            const isStarred = configData?.config?.starredMetrics?.includes(metric.id) || false;
            const customMetricDef = configData?.config?.customMetrics?.find((m) => m.id === metric.id);
            const isCustom = !!customMetricDef;

            return (
              <div key={metric.id} className="border border-adv-border rounded-adv-md bg-adv-card">
                <div className="flex items-center justify-between px-3 py-2">
                  <button
                    onClick={() => setExpandedMetric(isExpanded ? null : metric.id)}
                    className="flex items-center gap-2 flex-1 text-left text-adv-secondary rounded-adv-sm focus-visible:outline-none focus-visible:shadow-adv-focus"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="text-adv-body-m font-bold text-adv-primary">{metric.name}</span>
                    {hasGoal && (
                      <Target size={12} className="text-adv-accent" />
                    )}
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleStarred.mutate(metric.id)}
                      className={cn(
                        'p-1 rounded-adv-sm transition-colors',
                        isStarred
                          ? 'text-adv-gold hover:text-adv-gold'
                          : 'text-adv-tertiary hover:text-adv-gold'
                      )}
                      title={isStarred ? 'Unstar metric' : 'Star metric'}
                    >
                      <Star size={14} fill={isStarred ? 'currentColor' : 'none'} />
                    </button>
                    {isCustom && (
                      <button
                        onClick={() => handleEditMetric(customMetricDef)}
                        className="text-adv-tertiary hover:text-adv-primary p-1 rounded-adv-sm transition-colors"
                        title="Edit metric"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {!isFixed && (
                      <button
                        onClick={() => removeMetric.mutate(metric.id)}
                        className="text-adv-tertiary hover:text-adv-warning p-1 rounded-adv-sm transition-colors"
                        title="Remove metric"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-3 pb-3">
                    <MetricGoalForm
                      metricId={metric.id}
                      goal={localGoals[metric.id]}
                      onChange={(goal) => handleGoalChange(metric.id, goal)}
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Add metric section */}
          <div className="pt-2 flex items-center gap-2">
            <div className="flex-1">
              <AddMetricDropdown
                availableColumns={availableColumns}
                enabledMetrics={enabledMetrics}
                onAddPredefined={(id) => addPredefined.mutate(id)}
                onCreateCustom={() => setIsBuilderOpen(true)}
              />
            </div>
            <button
              type="button"
              onClick={() => setIsAISuggestOpen(true)}
              title="Suggest metrics with Vera"
              className="flex-shrink-0 w-10 h-10 rounded-adv-md border border-adv-border-default hover:border-adv-gold hover:bg-adv-gold-veil transition-colors flex items-center justify-center overflow-hidden focus-visible:outline-none focus-visible:shadow-adv-focus"
            >
              <img src="/vera.gif" alt="Suggest with Vera" className="w-8 h-8 object-cover" />
            </button>
          </div>
        </div>

        {/* Footer with save */}
        <div className="border-t border-adv-border px-3 sm:px-4 py-3 flex flex-col-reverse sm:flex-row justify-end gap-2">
          <button
            onClick={onClose}
            className="adv-btn adv-btn--ghost"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || updateGoals.isPending}
            className="adv-btn adv-btn--primary"
          >
            {updateGoals.isPending ? 'Saving...' : 'Save Goals'}
          </button>
        </div>
      </div>

      <CustomMetricBuilder
        isOpen={isBuilderOpen}
        onClose={() => {
          setIsBuilderOpen(false);
          setEditingMetric(null);
        }}
        availableColumns={availableColumns}
        sampleData={sampleData}
        editingMetric={editingMetric}
        onCreate={handleCreateCustom}
        onUpdate={handleUpdateCustom}
      />

      <AIMetricSuggestionsModal
        isOpen={isAISuggestOpen}
        onClose={() => setIsAISuggestOpen(false)}
        executionId={executionId}
        metadata={metadata}
        availableColumns={availableColumns}
        existingMetrics={availableMetrics}
        campaignData={campaignData}
      />
    </div>,
    document.body
  );
}
